import moment from 'moment'
import { rem } from 'polished'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'

import CreateNewTripTile from './CreateNewTripTile'
import MyTripsItemTile from './MyTripsItemTile'

import type { TripPlansSource } from 'analytics/eventDefinitions'
import {
  tripPlansFilterOptionSelected,
  tripPlansSortOptionSelected,
} from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import FilterChip from 'LuxKit/Chips/FilterChip'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineSlidersVIcon from 'LuxKit/Icons/line/LineSlidersVIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import ModalBase from 'LuxKit/Modal/ModalBase'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import useToggle from 'hooks/useToggle'
import { partitionBy, sortBy } from 'lib/array/arrayUtils'
import MyTripsSortAndFilter from 'tripPlanner/components/TripModal/MyTrips/SortAndFilter'
import {
  TRIP_PLANS_LAST_SELECTED_FILTERS_OPTION_STORAGE_KEY,
  TRIP_PLANS_LAST_SELECTED_SORT_OPTION_STORAGE_KEY,
} from 'tripPlanner/config'
import type { BasicTrip, FullTrip } from 'tripPlanner/types/common'
import {
  getTripViewedTimes,
  sortTripsByMostRecentlyViewedOrUpdated,
} from 'tripPlanner/utils/recentTrips'
import { useClearNewlyAddedTrips } from 'tripPlanner/hooks/api/userSettings'
import Sheet from 'components/utils/Sheet'
import TextButton from 'LuxKit/Button/TextButton'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'

interface Props {
  trips: Array<BasicTrip>
  source: TripPlansSource
  isVisibleCreateNewTripTile: boolean
  className?: string
  onCreateTrip?: (trip: FullTrip) => void
  maxTripsToShow?: number
}

const TripPanelGrid = styled.div`
  display: grid;
  gap: ${rem(20)};
  grid-template-columns: 1fr;
  grid-template-rows: auto;

  ${mediaQueryUp.desktop} {
    grid-template-columns: repeat(2, 1fr);
    justify-items: center;
    align-items: start;
    gap: ${rem(40)} ${rem(24)};
  }

  ${mediaQueryUp.largeDesktop} {
    grid-template-columns: repeat(3, 1fr);

    &.drawer {
      grid-template-columns: repeat(2, 1fr);
    }
  }
`

export const MY_TRIPS_SORT_OPTIONS = [
  'last edited',
  'date created',
  'upcoming',
  'alphabetical',
] as const
export type MyTripsSortOptions = (typeof MY_TRIPS_SORT_OPTIONS)[number]

export const MY_TRIPS_FILTER_OPTIONS = [
  'owned by me',
  'shared with me',
] as const
export type MyTripsFilterOptions = (typeof MY_TRIPS_FILTER_OPTIONS)[number]

const sortOptionMessages = defineMessages<MyTripsSortOptions>({
  'last edited': {
    id: 'Npt2RXgheoa4dLcPfNR5',
    defaultMessage: 'Last edited',
    description: 'Sort option label: sort trips by last edited date in the trip planner',
  },
  'date created': {
    id: 'C3dAm6IKzyZre577Qbwq',
    defaultMessage: 'Date created',
    description: 'Sort option label: sort trips by creation date in the trip planner',
  },
  'upcoming': {
    id: 'qdJA6XD2IvR9TowR0ISU',
    defaultMessage: 'Upcoming',
    description: 'Sort option label: sort trips by upcoming start date',
  },
  'alphabetical': {
    id: 'yRuEpo90nwBzyvMF3bQ6',
    defaultMessage: 'Alphabetical',
    description: 'Sort option label: sort trips alphabetically by name',
  },
})

const filterOptionMessages = defineMessages<MyTripsFilterOptions>({
  'owned by me': {
    id: 'OFq2qD9Pod8GBb1y-xSp',
    defaultMessage: 'Owned by me',
    description: 'Filter option label: show only trips owned by current user',
  },
  'shared with me': {
    id: 'eJrIhvfvF-tHYB8LdCpJ',
    defaultMessage: 'Shared with me',
    description: 'Filter option label: show only trips shared with current user',
  },
})

export function getMyTripsSortOptionLabel(
  intl: ReturnType<typeof useIntl>,
  option: MyTripsSortOptions,
): string {
  return intl.formatMessage(sortOptionMessages[option])
}

export function getMyTripsFilterOptionLabel(
  intl: ReturnType<typeof useIntl>,
  option: MyTripsFilterOptions,
): string {
  return intl.formatMessage(filterOptionMessages[option])
}

export default function MyTripsListGrid({
  trips,
  source,
  isVisibleCreateNewTripTile,
  className,
  onCreateTrip,
  maxTripsToShow,
}: Props) {
  const intl = useIntl()
  const [selectedFilter, setSelectedFilter] =
    useState<MyTripsFilterOptions | null>(null)
  const numberOfUnownedTrips = trips.filter((t) => t.role !== 'OWNER').length
  const [sortOption, setSortOption] =
    useState<MyTripsSortOptions>('last edited')
  const [showAllTrips, setShowAllTrips] = useState(false)

  const sortChipRef = useRef(null)

  const handleViewAllTrips = useCallback(() => {
    setShowAllTrips(true)
  }, [])

  const handleViewLessTrips = useCallback(() => {
    setShowAllTrips(false)
  }, [])

  const { value: isDropdownOpen, toggle: toggleDropdown } = useToggle()
  const onApplySortOption = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const sortOption = e.currentTarget.dataset.option as MyTripsSortOptions
      if (!sortOption) return
      setSortOption(sortOption)
      toggleDropdown()
      localStorage.setItem(
        TRIP_PLANS_LAST_SELECTED_SORT_OPTION_STORAGE_KEY,
        JSON.stringify(sortOption),
      )
      fireInteractionEvent(tripPlansSortOptionSelected(sortOption, source))
    },
    [setSortOption, toggleDropdown, source],
  )

  const onApplyFilterOption = useCallback(
    (filterOption: MyTripsFilterOptions | null) => {
      if (selectedFilter === filterOption || !filterOption) {
        setSelectedFilter(null)
        localStorage.removeItem(
          TRIP_PLANS_LAST_SELECTED_FILTERS_OPTION_STORAGE_KEY,
        )
        fireInteractionEvent(tripPlansFilterOptionSelected(null, source))
      } else {
        setSelectedFilter(filterOption)
        localStorage.setItem(
          TRIP_PLANS_LAST_SELECTED_FILTERS_OPTION_STORAGE_KEY,
          JSON.stringify(filterOption),
        )
        fireInteractionEvent(
          tripPlansFilterOptionSelected(filterOption, source),
        )
      }
    },
    [selectedFilter, source],
  )
  const showModal = useContext(ModalContext)

  const toggleSortAndFilterMenu = useCallback(() => {
    showModal(
      <ModalBase>
        <MyTripsSortAndFilter
          onFilterApply={(opt) => onApplyFilterOption(opt)}
          onSortApply={(opt) => setSortOption(opt)}
          selectedSortOption={sortOption}
          selectedFilter={selectedFilter}
          isFilterVisible={numberOfUnownedTrips > 0}
        />
      </ModalBase>,
    )
  }, [
    onApplyFilterOption,
    showModal,
    selectedFilter,
    sortOption,
    numberOfUnownedTrips,
  ])

  // Intiialise stored sort & filter options
  useEffect(() => {
    const storedSortOption = localStorage.getItem(
      TRIP_PLANS_LAST_SELECTED_SORT_OPTION_STORAGE_KEY,
    )

    if (storedSortOption) {
      const parsedOption = JSON.parse(storedSortOption)
      if (MY_TRIPS_SORT_OPTIONS.includes(parsedOption)) {
        setSortOption(parsedOption)
      }
    }

    const storedFilterOption = localStorage.getItem(
      TRIP_PLANS_LAST_SELECTED_FILTERS_OPTION_STORAGE_KEY,
    )

    if (storedFilterOption) {
      const parsedOption = JSON.parse(storedFilterOption)
      if (
        MY_TRIPS_FILTER_OPTIONS.includes(parsedOption) &&
        numberOfUnownedTrips > 0
      ) {
        setSelectedFilter(parsedOption)
      } else {
        setSelectedFilter(null)
        localStorage.removeItem(
          TRIP_PLANS_LAST_SELECTED_FILTERS_OPTION_STORAGE_KEY,
        )
      }
    }
  }, [numberOfUnownedTrips])

  const processedTrips = useMemo(() => {
    let processed: Array<BasicTrip> = [...trips]

    // Apply filter
    processed = [
      ...processed.filter((t) => {
        if (!selectedFilter) {
          return true
        }

        if (selectedFilter === 'owned by me') {
          return t.role === 'OWNER'
        }

        if (selectedFilter === 'shared with me') {
          return t.role !== 'OWNER'
        }

        return false
      }),
    ]

    // Apply sorting
    switch (sortOption) {
      case 'date created':
        processed = [...sortBy(processed, (trip) => trip.createdAt, 'desc')]
        break
      case 'last edited':
        processed = [
          ...sortTripsByMostRecentlyViewedOrUpdated(
            processed,
            getTripViewedTimes(),
          ),
        ]
        break
      case 'upcoming':
        // Sorts all upcoming trips first by start date in descending order,
        // then those without dates
        // then those that are past
        const now = moment()
        const [upcoming, past] = partitionBy(processed, (trip) => {
          return !trip.endDate || trip.endDate.clone().add(1, 'd') > now
        })

        processed = [
          ...sortBy(
            upcoming.filter((trip) => !!trip.startDate),
            (trip) => (trip?.startDate ? trip.startDate : undefined),
            'asc',
          ),
          ...upcoming.filter((trip) => !trip.startDate),
          ...sortBy(
            past,
            (trip) => (trip?.endDate ? trip.endDate : undefined),
            'desc',
          ),
        ]
        break
      case 'alphabetical':
        processed = [...sortBy(processed, (trip) => trip.name, 'asc')]
        break
    }

    return processed
  }, [trips, selectedFilter, sortOption])

  const sortAndFilterLabel = intl.formatMessage({
    id: 't33FpOqyLmp6glXQLLS9',
    defaultMessage: 'Sort & Filter',
    description: 'Mobile chip label for sort and filter options on trips list',
  })

  const sortLabelText = intl.formatMessage(
    {
      id: 'tC_m87A_M7gAs2FpbHKc',
      defaultMessage: 'Sort: {sortOption}',
      description: 'Chip label showing current sort option on trips list',
    },
    { sortOption: getMyTripsSortOptionLabel(intl, sortOption) },
  )

  const mobileSortAndFilterLabel = numberOfUnownedTrips > 0 ? sortAndFilterLabel : sortLabelText

  useClearNewlyAddedTrips()
  const numberOfTrips = trips.length
  return (
    <Sheet padding="0 0 48 0" className={className}>
      <Group direction="vertical" gap={24}>
        {trips.length > 1 && (
          <>
            <CSSBreakpoint max="mobile">
              <FilterChip
                onClick={toggleSortAndFilterMenu}
                startIcon={<LineSlidersVIcon />}
                size="medium"
              >
                {mobileSortAndFilterLabel}
              </FilterChip>
            </CSSBreakpoint>
            <CSSBreakpoint min="tablet">
              <Group
                direction="horizontal"
                horizontalAlign="start"
                verticalAlign="center"
              >
                <Group direction="horizontal" gap={12} verticalAlign="center">
                  <FilterChip
                    ref={sortChipRef}
                    style={{ zIndex: 1 }}
                    onClick={toggleDropdown}
                    size="medium"
                    endIcon={<LineAngleDownIcon />}
                  >
                    {sortLabelText}
                  </FilterChip>
                  <DropdownList
                    size="S"
                    placement="bottom"
                    open={isDropdownOpen}
                    triggerRef={sortChipRef}
                    anchorRef={sortChipRef}
                    onClose={toggleDropdown}
                  >
                    {MY_TRIPS_SORT_OPTIONS.map((opt) => (
                      <ClickableListItem
                        key={opt}
                        data-option={opt}
                        onClick={onApplySortOption}
                        titleFormat="propercase"
                        title={getMyTripsSortOptionLabel(intl, opt)}
                        selected={sortOption === opt}
                      />
                    ))}
                  </DropdownList>
                  {numberOfUnownedTrips > 0 && MY_TRIPS_FILTER_OPTIONS.map((opt) => (
                    <FilterChip
                      key={opt}
                      selected={selectedFilter === opt}
                      onClick={() => onApplyFilterOption(opt)}
                      size="medium"
                    >
                      {getMyTripsFilterOptionLabel(intl, opt)}
                    </FilterChip>
                  ))}
                </Group>
              </Group>
            </CSSBreakpoint>
          </>
        )}
        <TripPanelGrid>
          {isVisibleCreateNewTripTile && (
            <CreateNewTripTile onCreateTrip={onCreateTrip} />
          )}
          {(!!maxTripsToShow && !showAllTrips ? processedTrips.slice(0, maxTripsToShow) : processedTrips).map((trip) => (
            <MyTripsItemTile
              key={trip.id}
              trip={trip}
              source={source}
            />
          ))}
        </TripPanelGrid>
        {!!maxTripsToShow && numberOfTrips > maxTripsToShow && !showAllTrips && (
          <Group direction="vertical" horizontalAlign="center">
            <TextButton
              kind="secondary"
              onClick={handleViewAllTrips}
              endIcon={<LineAngleDownIcon />}
            >
              {intl.formatMessage(
                {
                  id: '3AeUl9iivi9BX_sj88sZ',
                  defaultMessage: 'View all {count} trips',
                  description: 'Button text to expand and show all remaining trips in the list',
                },
                { count: numberOfTrips - maxTripsToShow },
              )}
            </TextButton>
          </Group>
        )}
        {!!maxTripsToShow && numberOfTrips > maxTripsToShow && showAllTrips && (
          <Group direction="vertical" horizontalAlign="center">
            <TextButton
              kind="secondary"
              onClick={handleViewLessTrips}
              endIcon={<LineAngleUpIcon />}
            >
              {intl.formatMessage({
                id: 'HpT_ZRPvpoMHT8ise-O7',
                defaultMessage: 'View less',
                description: 'Button text to collapse the trips list to show fewer trips',
              })}
            </TextButton>
          </Group>
        )}
      </Group>
    </Sheet>
  )
}
